/**
 * `/metal-resources/tools-calculators/`: the Ryerson index of the site's two calculators. It
 * renders the hero, a card for each calculator
 * (`/metal-resources/tools-calculators/weight-calculator/` and `.../sheet-scrap-calculator/`), the
 * shared contact-us block and the "Metal Resources" cards. All copy is in
 * `src/content/ryerson/metal-resources/tools-calculators/index.ts`.
 *
 * Built only for Ryerson; on any other `NEXT_PUBLIC_COMPANY` the page is a 404. The two calculator
 * pages it links to are built for both brands, and the Southern Tool Steel `/metal-resources/`
 * landing links to them directly (`components/MetalResources/MetalResourcesLandingSTS.tsx`).
 */
import React from "react";
import { useApplication } from "@ryerson/frontend.application";
import { ContentSection } from "@ryerson/frontend.layout";
import { Media } from "@ryerson/frontend.next";
import { DefaultContactUsContent } from "@components/Shared/ContactUsRefactor/Content";
import ContactUsDesktop from "@components/Shared/ContactUsRefactor/Desktop/ContactUs";
import ContactUsMobile from "@components/Shared/ContactUsRefactor/Mobile/ContactUs";
import ReferencesHero from "@components/References/Hero/Hero";
import { HeroContent } from "@content/ryerson/metal-resources/tools-calculators";
import MetalResources from "@components/References/MetalResources/MetalResources";
import { MetalResourcesCustomContent } from "@content/ryerson/metal-resources/tools-calculators";
import SubPagesComponent from "@components/References/SubPages/SubPages";
import { SubPages } from "@content/ryerson/metal-resources/tools-calculators";
import MetaHead from "@components/Shared/model/MetaHead";
import { MetaObject } from "@content/ryerson/metal-resources/tools-calculators";
import { allContentfulHeaderCalls, HeaderPageProps } from "@ryerson/frontend.contentful";
import { getHeaderData } from "@components/NextLayouts/nextUtilities";
import HeaderLayout from "@components/NextLayouts/HeaderLayout";

/**
 * Build time: header, footer and locations data from Contentful; `notFound` off the Ryerson
 * build.
 */
export const getStaticProps = async () => {
	if (process.env.NEXT_PUBLIC_COMPANY !== "ryerson") {
		return { notFound: true }; // tells Next.js to skip page generation and return 404
	}

	return await allContentfulHeaderCalls();
};

/** The tools-and-calculators index page. */
const Page: React.FC<HeaderPageProps> = (props) => {
	const {
		localization: { language },
	} = useApplication();

	return (
		<HeaderLayout {...getHeaderData(props)}>
			<MetaHead content={MetaObject[language]} />
			<ReferencesHero content={HeroContent[language]} />
			<SubPagesComponent content={SubPages[language]} />
			<Media greaterThanOrEqual="lg">
				<ContentSection vPadding="30px" type="secondary" hPadding="0px">
					<ContactUsDesktop
						content={DefaultContactUsContent[language]}
						withImage={false}
						oneButtonVariant={false}
						background="secondary"
					/>
				</ContentSection>
			</Media>
			<Media lessThan="lg">
				<ContactUsMobile
					withImage={true}
					content={DefaultContactUsContent[language]}
					background="secondary"
					oneButtonVariant={false}
				/>
			</Media>
			<MetalResources content={MetalResourcesCustomContent[language]} />
		</HeaderLayout>
	);
};

export default Page;
